import { UserError, uuid } from '@zyno-io/vue-foundation';
import { compact } from 'lodash';
import { ref, type Ref } from 'vue';

import { GlobalState } from '@/global';
import { OtelState } from '@/otel';
import { Logger } from '@/shared/services/logger';

export interface IMobileConfig {
    platform: 'ios' | 'android';
    platformVersion: string;
    appVersion: string;
    deviceId: string;
    insets: { top: number; left: number; bottom: number; right: number };
    isStripeTerminal: boolean;
    isTapToPayCapable: boolean;
    hasDisplayedTTPEducation: boolean;
    idleTimeoutSeconds: number | null;
    rmmAuth: {
        supported: boolean;
        minimumIosVersion: string;
        minimumAndroidVersion: number;
        reason?: 'ios-version-too-old' | 'android-version-too-old' | 'platform-not-supported' | 'invalid-platform-version';
    };
}

// Keyboard geometry pushed from the native shell (see zynosuite-mobile's
// WebViewKeyboard.ts). `height` is CSS px at the WebView's normal scale;
// `nativeViewportResized` is true on Android (native already shrank the
// viewport, so dvh handles it) and false on iOS (we must offset manually).
export interface IKeyboardBroadcast {
    visible: boolean;
    height: number;
    duration: number;
    nativeViewportResized: boolean;
}

export type RmmInputMode = 'none' | 'terminal' | 'screen';
export type ActiveRmmInputMode = Exclude<RmmInputMode, 'none'>;
export type RmmInputModifier = 'ControlLeft' | 'AltLeft' | 'MetaLeft' | 'ShiftLeft';
export interface IRmmInputBroadcast {
    kind: 'text' | 'key' | 'secure-attention';
    text?: string;
    code?: string;
    modifiers: RmmInputModifier[];
}

interface IRmmInputEnvelope extends IRmmInputBroadcast {
    mode?: ActiveRmmInputMode;
}

interface IRmmFullscreenBroadcast {
    mode?: ActiveRmmInputMode;
    enabled: boolean;
}

export interface IMobileBridgeMessages {
    setTitle: {
        request: { title: string };
        response: boolean;
    };
    getConfig: {
        request: undefined;
        response: IMobileConfig;
    };
    // Tell the native shell which theme the web app is rendering, so it can match the status bar
    // style and the safe-area / bounce background. Without this a dark web app sits inside a light
    // native frame — most visibly as a white band under the notch and above the home indicator.
    //
    // `canvas` and `chrome` are resolved hex values read off the live tokens, so native never has
    // to carry a copy of the palette: `canvas` is the content background (`--bg`), `chrome` the
    // sidebar//status-bar background (`--chrome`). `style` is what the OS actually needs for the
    // status-bar glyphs — 'light' means light *content* on a dark bar.
    setAppearance: {
        request: {
            theme: 'light' | 'dark';
            style: 'light' | 'dark';
            canvas: string;
            chrome: string;
        };
        response: boolean;
    };
    getHeadersForRequest: {
        request: {
            method: string;
            url: string;
            headers: Record<string, string>;
            isAuthRequest?: boolean;
        };
        response: {
            headers: Record<string, string>;
        };
    };
    readPayment: {
        request: {
            clientSecret: string;
            // true → Tap to Pay; false → physical reader (Bluetooth / Stripe Terminal built-in);
            // omitted → device's current reader-mode preference. Also persists as that preference.
            tapToPay?: boolean;
        };
        response: { paymentIntentId: string };
    };
    cancelPayment: {
        request: undefined;
        response: boolean;
    };
    showBarcodeScanner: {
        request: undefined;
        response: boolean;
    };
    // Hand a 6-digit device pairing code to the native BLE Wi-Fi provisioning flow.
    // Resolves once the native sheet closes: `configured` reflects whether Wi-Fi was
    // set up (false if the user backed out), with the joined `ssid` on success.
    showBleWifiSetup: {
        request: { code: string };
        response: { configured: boolean; ssid?: string };
    };
    showToast: {
        request: { message: string; error?: boolean };
        response: boolean;
    };
    // Open another app by its URL scheme (e.g. the standalone ZynoTalk app). Native tries the deep
    // link and falls back to `fallbackUrl` (the app's store listing) when it isn't installed.
    // `opened` is true if the app launched, false if the user was sent to the store instead.
    openExternalApp: {
        request: { url: string; fallbackUrl: string };
        response: { opened: boolean };
    };
    beginFileDownload: {
        request: { filename: string; expectedBytes?: number };
        response: { downloadId: string };
    };
    appendFileDownload: {
        request: { downloadId: string; base64: string; byteLength: number };
        response: { writtenBytes: number };
    };
    finishFileDownload: {
        request: { downloadId: string };
        response: { presented: boolean; completed: boolean };
    };
    cancelFileDownload: {
        request: { downloadId: string };
        response: { cancelled: boolean };
    };
    storeRmmUnlock: {
        request: { accountId: string; tenantId: string; identityTenantId?: string; credentialId: string; prfKeyBase64: string; expiresAt: number };
        response: { stored: boolean; expiresAt: number };
    };
    getRmmUnlock: {
        request: { accountId: string; tenantId: string; identityTenantId?: string };
        response: { unlock: { credentialId: string; prfKeyBase64: string; expiresAt: number } | null };
    };
    clearRmmUnlock: {
        request: { accountId: string; tenantId: string; identityTenantId?: string } | undefined;
        response: { cleared: boolean };
    };
    setRmmInputContext: {
        request: { mode: RmmInputMode };
        response: { mode: RmmInputMode };
    };
    setRmmFullscreen: {
        request: { enabled: boolean };
        response: { enabled: boolean };
    };
    activateTapToPay: {
        request: undefined;
        response: { activated: boolean };
    };
    // Re-validate the session after an API 401. If the session is gone, native
    // resets its own navigation (Startup/Login) and reports where it ejected the
    // user; `{ valid: true }` means stay put, `{ valid: false }` with no
    // `ejectedTo` means the check itself failed (e.g. offline).
    checkSession: {
        request: undefined;
        response: { valid: boolean; ejectedTo?: 'top' | 'login' };
    };
    logout: {
        request: undefined;
        response: boolean;
    };
}

const BARCODE_SCAN_PREFIX = 'ZBarcodeScan:';
const KEYBOARD_BROADCAST_PREFIX = 'ZBroadcast:Keyboard:';
const RMM_INPUT_BROADCAST_PREFIX = 'ZBroadcast:RmmInput:';
const RMM_FULLSCREEN_BROADCAST_PREFIX = 'ZBroadcast:RmmFullscreen:';

const logger = Logger.createScoped('MobileBridge');

export class MobileBridge {
    static isWithinZynoSuiteMobile = false;
    // Reactive so screens (e.g. the launcher Tap to Pay awareness card) react to
    // the config arriving asynchronously after `getConfig` resolves post-boot.
    static config: Ref<IMobileConfig | null> = ref(null);
    // Latest keyboard geometry from the native shell. Modals lift off it via the
    // --zs-keyboard-inset CSS var (set in applyKeyboard); this ref lets other
    // surfaces (e.g. the salespoint cart) react in JS if they need to.
    static keyboard: Ref<IKeyboardBroadcast | null> = ref(null);
    // oxlint-disable-next-line no-explicit-any
    static resolvers: Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void }> = new Map();
    static barcodeScanListeners: Set<(code: string) => void> = new Set();
    static activeRmmInputMode: RmmInputMode = 'none';
    static rmmInputListeners: Set<{ mode: ActiveRmmInputMode; listener: (input: IRmmInputBroadcast) => void }> = new Set();
    static rmmFullscreenListeners: Set<{ mode: ActiveRmmInputMode; listener: (enabled: boolean) => void }> = new Set();

    static init() {
        if (import.meta.env.VITE_APP_ENABLE_FAKE_MOBILE_BRIDGE === 'true') {
            GlobalState.enableMobileAppDesign.value = true;
        }

        try {
            if (!/ ZMA:app.zyno.suite(-(dev|alpha))?\//.test(navigator.userAgent)) return logger.info('init: user agent does not match');
            this.isWithinZynoSuiteMobile = true;
            GlobalState.enableMobileAppDesign.value = true;

            logger.info('Detected ZynoSuite Mobile WebView environment');
            window.addEventListener('message', event => {
                if (typeof event.data !== 'string') return;
                if (event.data.startsWith('ZIPCResponse:')) return this.handleIncomingMessage(event.data.substring(13));
                if (event.data.startsWith(BARCODE_SCAN_PREFIX)) return this.emitBarcodeScan(event.data.substring(BARCODE_SCAN_PREFIX.length));
                if (event.data.startsWith(KEYBOARD_BROADCAST_PREFIX))
                    return this.applyKeyboard(event.data.substring(KEYBOARD_BROADCAST_PREFIX.length));
                if (event.data.startsWith(RMM_INPUT_BROADCAST_PREFIX))
                    return this.emitRmmInput(event.data.substring(RMM_INPUT_BROADCAST_PREFIX.length));
                if (event.data.startsWith(RMM_FULLSCREEN_BROADCAST_PREFIX))
                    return this.emitRmmFullscreen(event.data.substring(RMM_FULLSCREEN_BROADCAST_PREFIX.length));
            });

            logger.info('MobileBridge initialized successfully');
            this.loadConfig();
        } catch (err) {
            logger.error('Error initializing MobileBridge', err);
        }
    }

    private static async loadConfig() {
        logger.info('Loading mobile configuration');
        const config = await this.sendRequest('getConfig', undefined);
        this.config.value = config;
        OtelState.workstationId = config.deviceId;
        this.applyInsets(config.insets);
        logger.info('Mobile configuration loaded', { config });
    }

    // Publish the native safe-area insets as CSS custom properties so the shell,
    // modals, and screens can pad around the notch/home-indicator. We can't rely
    // on CSS env(safe-area-inset-*) here — the RN WebView doesn't expose it — so
    // the native side hands us the numbers and we mirror them onto :root.
    private static applyInsets(insets: IMobileConfig['insets']) {
        const root = document.documentElement.style;
        root.setProperty('--zs-inset-top', `${insets.top}px`);
        root.setProperty('--zs-inset-right', `${insets.right}px`);
        root.setProperty('--zs-inset-bottom', `${insets.bottom}px`);
        root.setProperty('--zs-inset-left', `${insets.left}px`);
    }

    // Publish keyboard geometry so overlays can lift above the on-screen
    // keyboard. Modals consume --zs-keyboard-inset purely in CSS (see
    // 06_mobile.css). We resolve the inset here rather than in CSS: Android
    // already shrinks the WebView viewport (dvh reflects it), so it contributes
    // 0 and only iOS's non-resized viewport gets the real height.
    private static applyKeyboard(payloadJson: string) {
        try {
            const payload: IKeyboardBroadcast = JSON.parse(payloadJson);
            this.keyboard.value = payload;
            const inset = payload.visible && !payload.nativeViewportResized ? payload.height : 0;
            const root = document.documentElement.style;
            root.setProperty('--zs-keyboard-inset', `${inset}px`);
            root.setProperty('--zs-keyboard-duration', `${payload.duration}ms`);
        } catch (err) {
            logger.error('Failed to apply keyboard broadcast', err);
        }
    }

    static sendRequest<T extends keyof IMobileBridgeMessages>(
        type: T,
        data: IMobileBridgeMessages[T]['request'],
        options: { timeoutMs?: number } = {}
    ): Promise<IMobileBridgeMessages[T]['response']> {
        return new Promise<IMobileBridgeMessages[T]['response']>((resolve, reject) => {
            const requestId = uuid();
            let timeoutHandle: number | undefined;
            const resolver = {
                resolve: (value: IMobileBridgeMessages[T]['response']) => {
                    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
                    resolve(value);
                },
                reject: (reason?: unknown) => {
                    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
                    reject(reason);
                }
            };
            this.resolvers.set(requestId, resolver);
            if (options.timeoutMs !== undefined) {
                timeoutHandle = window.setTimeout(() => {
                    if (this.resolvers.get(requestId) !== resolver) return;
                    this.resolvers.delete(requestId);
                    reject(new Error(`Native ${String(type)} request timed out after ${options.timeoutMs}ms`));
                }, options.timeoutMs);
            }
            try {
                // oxlint-disable-next-line no-explicit-any
                (window as any).ReactNativeWebView.postMessage(`ZIPCRequest:${JSON.stringify({ type, data, requestId })}`);
            } catch (err) {
                this.resolvers.delete(requestId);
                resolver.reject(err);
            }
        });
    }

    private static handleIncomingMessage(messageJson: string) {
        const message = JSON.parse(messageJson);
        const { requestId, data, error } = message;
        const resolver = this.resolvers.get(requestId);
        if (resolver) {
            if (error) {
                const msg = compact([`${error.message}`, !!error.code && `[${error.code}]`]).join(' ');
                const err = 'isUserError' in error && error.isUserError ? new UserError(msg) : new Error(msg);
                resolver.reject(err);
            } else {
                resolver.resolve(data);
            }
            this.resolvers.delete(requestId);
        }
    }

    // Ask the native side to re-validate the session, used when an API call 401s
    // inside the webview. Native ejects the user (resetting its own navigation) if
    // the session is gone — the SPA never shows its web login screen in the app.
    // Deduped so a burst of parallel 401s only triggers a single round-trip.
    private static checkSessionPromise: Promise<IMobileBridgeMessages['checkSession']['response']> | null = null;
    static checkSession() {
        if (!this.isWithinZynoSuiteMobile) return;
        this.checkSessionPromise ??= this.sendRequest('checkSession', undefined)
            .catch(err => {
                logger.error('checkSession failed', err);
                return { valid: false } as const;
            })
            .finally(() => {
                this.checkSessionPromise = null;
            });
        return this.checkSessionPromise;
    }

    // Ask the native side to log the user out (e.g. after a web-side idle timeout).
    // Native tears down its own session/navigation; the SPA never shows a web login.
    static logout() {
        if (!this.isWithinZynoSuiteMobile) return;
        return this.sendRequest('logout', undefined).catch(err => {
            logger.error('logout failed', err);
            return false;
        });
    }

    // Ask the native shell to open an external app by its URL scheme (e.g. the standalone ZynoTalk
    // app), falling back to its store listing when the app isn't installed. Resolves `{ opened }` —
    // true if the app launched, false if the user was sent to the store. A no-op outside the native
    // app: the browser can't hand off to another app, so callers get `{ opened: false }`.
    static openExternalApp(url: string, fallbackUrl: string): Promise<IMobileBridgeMessages['openExternalApp']['response']> {
        if (!this.isWithinZynoSuiteMobile) return Promise.resolve({ opened: false });
        return this.sendRequest('openExternalApp', { url, fallbackUrl });
    }

    // Barcodes scanned by the native scanner (launched via `showBarcodeScanner`)
    // arrive as `ZBarcodeScan:<data>` pushes rather than request responses.
    static onBarcodeScan(listener: (code: string) => void): () => void {
        this.barcodeScanListeners.add(listener);
        return () => this.barcodeScanListeners.delete(listener);
    }

    private static emitBarcodeScan(code: string) {
        for (const listener of this.barcodeScanListeners) {
            listener(code);
        }
    }

    static setRmmInputContext(mode: RmmInputMode) {
        if (!this.isWithinZynoSuiteMobile) {
            this.activeRmmInputMode = 'none';
            return Promise.resolve({ mode: 'none' as const });
        }
        return this.sendRequest('setRmmInputContext', { mode })
            .then(result => {
                this.activeRmmInputMode = result.mode;
                return result;
            })
            .catch(err => {
                this.activeRmmInputMode = 'none';
                logger.warn('Failed to set native RMM input context', err);
                return { mode: 'none' as const };
            });
    }

    static setRmmFullscreen(enabled: boolean) {
        if (!this.isWithinZynoSuiteMobile) return Promise.resolve({ enabled: false });
        return this.sendRequest('setRmmFullscreen', { enabled }).catch(err => {
            logger.warn('Failed to set native RMM full screen mode', err);
            return { enabled: false };
        });
    }

    static onRmmFullscreen(mode: ActiveRmmInputMode, listener: (enabled: boolean) => void): () => void {
        const registration = { mode, listener };
        this.rmmFullscreenListeners.add(registration);
        return () => this.rmmFullscreenListeners.delete(registration);
    }

    private static emitRmmFullscreen(value: string) {
        let broadcast: IRmmFullscreenBroadcast;
        try {
            broadcast = value.startsWith('{') ? JSON.parse(value) : { enabled: value === 'true' };
        } catch (err) {
            logger.error('Failed to handle native RMM full screen state', err);
            return;
        }
        const mode = broadcast.mode ?? this.activeRmmInputMode;
        if (mode === 'none') return;
        this.activeRmmInputMode = mode;
        for (const registration of this.rmmFullscreenListeners) {
            if (registration.mode === mode) registration.listener(broadcast.enabled);
        }
    }

    static onRmmInput(mode: ActiveRmmInputMode, listener: (input: IRmmInputBroadcast) => void): () => void {
        const registration = { mode, listener };
        this.rmmInputListeners.add(registration);
        return () => this.rmmInputListeners.delete(registration);
    }

    private static emitRmmInput(payloadJson: string) {
        try {
            const payload: IRmmInputEnvelope = JSON.parse(payloadJson);
            const mode = payload.mode ?? this.activeRmmInputMode;
            if (mode === 'none') return;
            this.activeRmmInputMode = mode;
            for (const registration of this.rmmInputListeners) {
                if (registration.mode === mode) registration.listener(payload);
            }
        } catch (err) {
            logger.error('Failed to handle native RMM input', err);
        }
    }
}

MobileBridge.init();
